import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { MAX_FILE_MB, isPreviewable, checkFiles, uploadToStorage, registerAttachments, signedUrl, downloadAttachment, filesFromClipboard, sendEmail as postEmail } from '../lib/attachments'
import { CustomerLink } from './Customers'
import { Icon, Avatar, StatusPill, Spinner, STATUS, STATUS_ORDER, normStatus, timeAgo, fmtDateTime, formatSize, FileIcon, requesterName, splitQuoted, parseCustomerMessage } from './ui'

// Testo del messaggio: si vede solo la parte nuova, la parte citata/inoltrata resta chiusa
function MessageText({ text }) {
  const [open, setOpen] = useState(false)
  const { main, quoted, label } = splitQuoted(text)
  return (
    <>
      <div className="msg-body">{main}</div>
      {quoted && (
        <>
          <button className={`quote-toggle${open ? ' open' : ''}`} onClick={() => setOpen(o => !o)} title={open ? 'Nascondi' : 'Mostra'}>
            <span className="quote-dots">•••</span>
            <span className="truncate">{open ? 'Nascondi' : label}</span>
          </button>
          {open && <div className="msg-quoted">{quoted}</div>}
        </>
      )}
    </>
  )
}

// Anteprima cliccabile per le immagini, chip scaricabile per gli altri file
function AttachmentView({ att, imageUrl, onDownload, onImgError }) {
  if (isPreviewable(att.file_type) && imageUrl) {
    return (
      <a className="att-thumb" href={imageUrl} target="_blank" rel="noopener noreferrer" title={att.file_name}>
        <img src={imageUrl} alt={att.file_name} onError={() => onImgError(att)} />
      </a>
    )
  }
  return (
    <button className="att-chip" onClick={() => onDownload(att)} title={`Scarica ${att.file_name}`}>
      <FileIcon type={att.file_type} size={15} />
      <span className="truncate">{att.file_name}</span>
      <span className="att-size">{formatSize(att.file_size)}</span>
    </button>
  )
}

// Riferimento interno del ticket: si salva uscendo dal campo o con Invio, Esc annulla
function RefField({ value, onSave }) {
  const [text, setText] = useState(value)
  const skipBlur = useRef(false)   // Invio ha gia' salvato, Esc ha annullato: l'uscita dal campo non salva di nuovo
  useEffect(() => { setText(value) }, [value])
  const save = async () => { if (!(await onSave(text))) setText(value) }
  return (
    <label className="field">
      <span className="label">Riferimento interno</span>
      <input className="input input-sm" value={text} maxLength={100} placeholder="Es. ordine 957, pratica GLS…"
        onChange={e => setText(e.target.value)}
        onFocus={() => { skipBlur.current = false }}
        onBlur={() => { if (skipBlur.current) { skipBlur.current = false; return } save() }}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); skipBlur.current = true; save(); e.currentTarget.blur() }
          if (e.key === 'Escape') { skipBlur.current = true; setText(value); e.currentTarget.blur() }
        }} />
      <span className="hint">Visibile solo agli operatori. Si trova con la ricerca dei ticket.</span>
    </label>
  )
}

// Chi riceve in copia le risposte pubbliche: si toglie con la x, si aggiunge scrivendo l'indirizzo
function CcEditor({ list, recipient, onSave }) {
  const [adding, setAdding] = useState(false)
  const [value, setValue] = useState('')
  const [error, setError] = useState('')
  const add = async () => {
    const e = value.trim().toLowerCase()
    if (!/^[^\s@,;<>"()]+@[^\s@,;<>"()]+\.[^\s@,;<>"()]+$/.test(e)) { setError('Indirizzo non valido.'); return }
    if (e === (recipient || '').toLowerCase() || list.includes(e)) { setError('Riceve già le risposte.'); return }
    if (await onSave([...list, e])) { setValue(''); setError(''); setAdding(false) }
  }
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
        <span className="label" style={{ margin: 0 }}>In copia nelle risposte</span>
        {!adding && <button className="btn-link" style={{ border: 0, background: 'none', color: 'var(--accent-text)', fontSize: 12, padding: 0 }} onClick={() => setAdding(true)}>+ Aggiungi</button>}
      </div>
      {list.length === 0 && !adding && <div className="hint">Nessuno: le risposte vanno solo al richiedente.</div>}
      {list.map(e => (
        <div key={e} className="row" style={{ gap: 6, fontSize: 13, padding: '2px 0' }}>
          <span className="grow truncate" title={e}>{e}</span>
          <button className="btn btn-ghost btn-sm btn-icon" style={{ width: 24, height: 24 }} title="Togli dalla copia"
            onClick={() => onSave(list.filter(x => x !== e))}><Icon name="x" size={12} /></button>
        </div>
      ))}
      {adding && (
        <div style={{ marginTop: 6 }}>
          <input className="input input-sm" placeholder="collega@azienda.it" value={value} autoFocus
            onChange={ev => { setValue(ev.target.value); setError('') }}
            onKeyDown={ev => { if (ev.key === 'Enter') add(); if (ev.key === 'Escape') setAdding(false) }} />
          <div className="row" style={{ gap: 6, marginTop: 6 }}>
            <button className="btn btn-primary btn-sm" onClick={add}><Icon name="check" size={13} /> Aggiungi</button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setAdding(false); setError('') }}>Annulla</button>
          </div>
          {error && <div className="hint" style={{ color: 'var(--red)' }}>{error}</div>}
        </div>
      )}
    </div>
  )
}

export default function TicketDetail({ ticket: initialTicket, profile, goBack, backLabel, onOpenCustomer }) {
  const [ticket, setTicket] = useState(initialTicket)
  const [comments, setComments] = useState([])
  const [attachments, setAttachments] = useState([])
  const [imageUrls, setImageUrls] = useState({})
  const [newComment, setNewComment] = useState('')
  const [replyMode, setReplyMode] = useState(true)
  const [profiles, setProfiles] = useState([])
  const [settings, setSettings] = useState({})
  const [saving, setSaving] = useState(false)
  const [sending, setSending] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [pendingFiles, setPendingFiles] = useState([])
  const [notice, setNotice] = useState(null)      // { type: 'error' | 'warning', text }
  const [dragOver, setDragOver] = useState(false)
  const [commentsReady, setCommentsReady] = useState(false)
  const imgTries = useRef({})
  const lastMsgRef = useRef(null)

  // Porta l'ultimo messaggio in cima alla vista (con l'editor sotto), solo se non e' gia' visibile
  const scrollToLast = (smooth) => {
    const el = lastMsgRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    if (r.top >= 0 && r.bottom <= window.innerHeight) return
    el.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'auto' })
  }

  // All'apertura del ticket si parte dall'ultimo messaggio
  useEffect(() => {
    if (commentsReady) requestAnimationFrame(() => scrollToLast(false))
  }, [commentsReady])

  useEffect(() => { loadComments(); loadProfiles(); loadTicket(); loadAttachments(); loadSettings() }, [])

  const loadSettings = async () => {
    const { data } = await supabase.from('settings').select('key, value')
      .in('key', ['mailbox1_email', 'mailbox1_operator_id', 'mailbox2_email', 'mailbox2_operator_id'])
    const m = {}
    ;(data || []).forEach(r => { m[r.key] = r.value })
    setSettings(m)
  }

  const loadTicket = async () => {
    const { data } = await supabase.from('tickets')
      .select('*, created_by_profile:profiles!tickets_created_by_fkey(name, email), assigned_to_profile:profiles!tickets_assigned_to_fkey(name)')
      .eq('id', initialTicket.id).single()
    if (data) setTicket(data)
  }

  const loadComments = async () => {
    const { data } = await supabase.from('comments')
      .select('*, author:profiles(name)')
      .eq('ticket_id', initialTicket.id)
      .order('created_at', { ascending: true })
    setComments(data || [])
    setCommentsReady(true)
  }

  const loadProfiles = async () => {
    const { data } = await supabase.from('profiles').select('id, name, email')
    setProfiles(data || [])
  }

  const loadAttachments = async () => {
    const { data } = await supabase.from('attachments')
      .select('*')
      .eq('ticket_id', initialTicket.id)
      .order('created_at', { ascending: true })
    setAttachments(data || [])
    signImages(data || [])
  }

  // URL firmati per le immagini mostrabili (bucket privato) → anteprima inline
  const signImages = async (atts) => {
    const imgs = (atts || []).filter(a => isPreviewable(a.file_type) && a.file_path)
    if (!imgs.length) return
    const entries = await Promise.all(imgs.map(async a => [a.id, await signedUrl(a.file_path)]))
    const map = {}
    entries.forEach(([id, url]) => { if (url) map[id] = url })
    if (Object.keys(map).length) setImageUrls(prev => ({ ...prev, ...map }))
  }

  // Anteprima che non si carica (link scaduto o file mancante): una nuova firma, poi si ripiega sul file scaricabile
  const handleImgError = async (att) => {
    const tries = imgTries.current[att.id] || 0
    imgTries.current[att.id] = tries + 1
    const url = tries === 0 ? await signedUrl(att.file_path) : null
    setImageUrls(prev => ({ ...prev, [att.id]: url }))
  }

  const updateTicket = async (field, value) => {
    setSaving(true)
    const { error } = await supabase.from('tickets').update({ [field]: value }).eq('id', ticket.id)
    setSaving(false)
    if (error) { console.error('Errore aggiornamento ticket:', error.message); return }
    setTicket(prev => {
      const next = { ...prev, [field]: value }
      if (field === 'assigned_to') next.assigned_to_profile = profiles.find(p => p.id === value) || null
      return next
    })
  }

  // Riferimento interno: solo per gli operatori, non va mai al cliente
  const saveRef = async (value) => {
    const v = value.trim().slice(0, 100) || null
    if (v === (ticket.internal_ref || null)) return true
    setSaving(true)
    const { error } = await supabase.from('tickets').update({ internal_ref: v }).eq('id', ticket.id)
    setSaving(false)
    if (error) { setNotice({ type: 'error', text: `Riferimento non salvato: ${error.message}` }); return false }
    setTicket(prev => ({ ...prev, internal_ref: v }))
    return true
  }

  // Indirizzi in copia nelle risposte (li raccoglie il worker dalle email del cliente)
  const ccList = Array.isArray(ticket.cc_emails) ? ticket.cc_emails : []
  const saveCc = async (list) => {
    setSaving(true)
    const { error } = await supabase.from('tickets').update({ cc_emails: list }).eq('id', ticket.id)
    setSaving(false)
    if (error) { setNotice({ type: 'error', text: `Copia non aggiornata: ${error.message}` }); return false }
    setTicket(prev => ({ ...prev, cc_emails: list }))
    return true
  }

  const addFiles = (files) => {
    if (!files.length) return
    setNotice(null)
    setPendingFiles(prev => [...prev, ...files])
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    addFiles(files)
    e.target.value = ''
  }

  // Ctrl+V di uno screenshot o di file copiati
  const handlePaste = (e) => {
    const files = filesFromClipboard(e)
    if (files.length) { e.preventDefault(); addFiles(files) }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    addFiles(Array.from(e.dataTransfer?.files || []))
  }

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index))
  }

  // Scarica col nome originale; se il file non c'e' lo dice invece di non fare nulla
  const downloadFile = async (att) => {
    try { await downloadAttachment(att) }
    catch (e) { setNotice({ type: 'error', text: `Impossibile scaricare "${att.file_name}": ${e.message}` }) }
  }

  // Casella da cui parte la risposta: quella dell'operatore assegnato; fallback alla casella del ticket
  const outgoingMailbox = (() => {
    if (ticket.assigned_to) {
      if (settings.mailbox1_operator_id === ticket.assigned_to && settings.mailbox1_email) return settings.mailbox1_email
      if (settings.mailbox2_operator_id === ticket.assigned_to && settings.mailbox2_email) return settings.mailbox2_email
    }
    return ticket.mailbox
  })()

  const canEmail = !!ticket.recipient_email
  const isPublic = canEmail && replyMode

  // Ordine sicuro: 1) file nello storage  2) messaggio  3) registrazione allegati  4) email.
  // Se i file non si caricano non nasce nessun messaggio, e testo e file restano per riprovare.
  const addComment = async (sendEmail = false) => {
    if (busy) return
    if (!newComment.trim() && pendingFiles.length === 0) return
    const problems = checkFiles(pendingFiles)
    if (problems.length) { setNotice({ type: 'error', text: `Allegati non validi: ${problems.join(', ')}. Rimuovili e riprova.` }); return }

    setNotice(null)
    setBusy(true)
    setSending(sendEmail)
    setUploading(pendingFiles.length > 0)
    try {
      const files = pendingFiles
      const up = await uploadToStorage(ticket.id, files)
      if (up.error) {
        setNotice({ type: 'error', text: `Allegato non caricato — ${up.error}. Il messaggio non è stato inviato: riprova, oppure rimuovi quel file.` })
        return
      }

      const content = newComment.trim() || `📎 ${files.length} allegato/i`
      // is_internal distingue la nota interna dalla risposta inviata al cliente.
      // Se la colonna non esiste ancora (SQL non eseguito) si salva senza, per non perdere il messaggio.
      const base = { ticket_id: ticket.id, author_id: profile.id, content }
      let { data, error: insErr } = await supabase.from('comments')
        .insert({ ...base, is_internal: !sendEmail }).select('*, author:profiles(name)').single()
      if (insErr && /is_internal/.test(insErr.message || '')) {
        ;({ data, error: insErr } = await supabase.from('comments').insert(base).select('*, author:profiles(name)').single())
      }
      if (!data) {
        if (up.uploaded.length) await supabase.storage.from('attachments').remove(up.uploaded.map(u => u.path)).catch(() => {})
        setNotice({ type: 'error', text: `Messaggio non salvato: ${insErr?.message || 'errore sconosciuto'}. Riprova.` })
        return
      }

      const reg = await registerAttachments({ ticketId: ticket.id, commentId: data.id, uploaded: up.uploaded, userId: profile.id })
      setComments(prev => [...prev, data])
      setTimeout(() => scrollToLast(true), 60)
      setAttachments(prev => [...prev, ...reg.rows])
      signImages(reg.rows)
      setNewComment('')
      setPendingFiles([])
      setReplyMode(true)
      if (reg.error) setNotice({ type: 'warning', text: `Messaggio salvato, ma gli allegati non sono stati registrati: ${reg.error}` })

      if (sendEmail && ticket.recipient_email) {
        try {
          const out = await postEmail({
            type: 'reply', ticket: { ...ticket }, message: content, mailbox: outgoingMailbox, comment_id: data.id,
            attachments: reg.rows.map(a => ({ path: a.file_path, name: a.file_name }))
          })
          if (out.missingAttachments?.length) {
            setNotice({ type: 'warning', text: `Email inviata, ma senza questi allegati: ${out.missingAttachments.join(', ')}.` })
          }
        } catch (e) {
          // L'email non e' partita: il messaggio non va mostrato come "inviato al cliente"
          const { error: resetErr } = await supabase.from('comments').update({ is_internal: null }).eq('id', data.id)
            .then(r => r, err => ({ error: err }))
          setComments(prev => prev.map(c => c.id === data.id ? { ...c, is_internal: null } : c))
          setNotice({ type: 'error', text: `Il messaggio è salvato nel ticket, ma l'email al cliente NON è partita (${e.message}). Riprova a inviarla.`
            + (resetErr ? ' Attenzione: ricaricando la pagina potrebbe risultare come inviato.' : '') })
        }
      }
    } finally {
      setSending(false); setUploading(false); setBusy(false)
    }
  }


  const st = normStatus(ticket.status)

  // Allegati per commento e allegati del ticket (senza commento)
  const attachmentsByComment = {}
  attachments.forEach(a => {
    if (a.comment_id) {
      if (!attachmentsByComment[a.comment_id]) attachmentsByComment[a.comment_id] = []
      attachmentsByComment[a.comment_id].push(a)
    }
  })
  const ticketAttachments = attachments.filter(a => !a.comment_id)

  // Il ticket nato da email (ha la casella) e' stato scritto dal cliente; altrimenti dall'operatore
  const fromCustomer = !!ticket.mailbox && !!ticket.recipient_email
  const openerName = fromCustomer ? requesterName(ticket.recipient_email) : (ticket.created_by_profile?.name || 'Operatore')
  const hasOpening = !!ticket.description || ticketAttachments.length > 0

  const renderAttachments = (list) => list.length > 0 && (
    <div className="att-row">
      {list.map(att => <AttachmentView key={att.id} att={att} imageUrl={imageUrls[att.id]} onDownload={downloadFile} onImgError={handleImgError} />)}
    </div>
  )

  const sendLabel = uploading ? 'Caricamento…' : sending ? 'Invio…' : isPublic ? 'Invia risposta' : 'Aggiungi nota'
  const canSend = (newComment.trim() || pendingFiles.length > 0) && !sending && !uploading && !busy
  const others = profiles.filter(p => p.id !== profile.id && p.id !== ticket.assigned_to)

  return (
    <div>
      <div className="crumbs" style={{ marginBottom: 12 }}>
        <button onClick={goBack}><Icon name="arrowLeft" size={15} /> {backLabel || 'Ticket'}</button>
        <span className="sep">/</span>
        <span className="truncate" style={{ maxWidth: 420 }}>{ticket.number != null ? `#${ticket.number}` : 'Ticket'}</span>
      </div>

      {/* Testata */}
      <div className="card detail-head">
        <div className="minw0" style={{ flex: '1 1 420px' }}>
          <div className="dh-meta">
            {ticket.number != null && <span className="dh-num">#{ticket.number}</span>}
            <StatusPill status={ticket.status} />
            {ticket.internal_ref && <span className="ref-chip" title="Riferimento interno"><Icon name="lock" size={11} /> {ticket.internal_ref}</span>}
            {saving && <span className="saving"><Spinner size={12} /> Salvataggio…</span>}
          </div>
          <h1 className="dh-title">{ticket.title}</h1>
          <div className="dh-sub">
            <Icon name="calendar" size={14} /> Aperto {timeAgo(ticket.created_at)}
            {fromCustomer && <><span className="faint">·</span> da <strong style={{ color: 'var(--text-2)' }}>{ticket.recipient_email}</strong></>}
            {ticket.mailbox && <><span className="faint">·</span> via {ticket.mailbox}</>}
          </div>
        </div>
        <div className="seg" role="group" aria-label="Stato del ticket">
          {STATUS_ORDER.map(s => (
            <button key={s} className={`seg-btn${st === s ? ' active' : ''}`} onClick={() => st !== s && updateTicket('status', s)}>
              <span className="pill-dot" style={{ background: STATUS[s].dot }} />
              {STATUS[s].label}
            </button>
          ))}
        </div>
      </div>

      <div className="detail-grid">
        {/* Conversazione */}
        <div className="card">
          <div className="card-header">
            <div className="row"><span className="card-title">Conversazione</span><span className="badge">{comments.length + (hasOpening ? 1 : 0)}</span></div>
            <span className="card-sub">{attachments.length > 0 && <><Icon name="paperclip" size={13} style={{ verticalAlign: '-2px' }} /> {attachments.length} allegat{attachments.length === 1 ? 'o' : 'i'}</>}</span>
          </div>

          <div className="timeline">
            {hasOpening && (
              <article className="msg msg-desc" ref={comments.length === 0 ? lastMsgRef : undefined}>
                <Avatar name={fromCustomer ? ticket.recipient_email : openerName} size={34} />
                <div className="msg-main">
                  <header className="msg-head">
                    <span className="msg-name">{openerName}</span>
                    <span className={`tag ${fromCustomer ? 'tag-customer' : 'tag-agent'}`}>{fromCustomer ? 'Cliente' : 'Operatore'}</span>
                    <span className="msg-via">Richiesta iniziale{fromCustomer && ' · via email'}</span>
                    <time className="msg-time" title={fmtDateTime(ticket.created_at)}>{fmtDateTime(ticket.created_at)}</time>
                  </header>
                  {ticket.description && <MessageText text={ticket.description} />}
                  {renderAttachments(ticketAttachments)}
                </div>
              </article>
            )}

            {comments.map((c, i) => {
              // Messaggi del cliente: dal worker (email) o dall'area clienti; is_internal resta vuoto
              const cm = parseCustomerMessage(c)
              const isCustomer = !!cm
              const content = isCustomer ? cm.text : (c.content || '')
              const sender = isCustomer ? (cm.sender || ticket.recipient_email || '') : ''
              const name = isCustomer ? (requesterName(sender) || 'Cliente') : (c.author?.name || 'Operatore')
              const commentAtts = attachmentsByComment[c.id] || []
              const isNote = !isCustomer && c.is_internal === true
              const isSent = !isCustomer && c.is_internal === false
              return (
                <article key={c.id} ref={i === comments.length - 1 ? lastMsgRef : undefined} className={`msg ${isCustomer ? 'msg-customer' : isNote ? 'msg-note' : 'msg-agent'}`}>
                  <Avatar name={isCustomer ? (sender || name) : name} size={34} />
                  <div className="msg-main">
                    <header className="msg-head">
                      <span className="msg-name">{name}</span>
                      {isNote
                        ? <span className="tag tag-note"><Icon name="lock" size={11} style={{ marginRight: 4 }} />Nota interna</span>
                        : <span className={`tag ${isCustomer ? 'tag-customer' : 'tag-agent'}`}>{isCustomer ? 'Cliente' : 'Operatore'}</span>}
                      {isCustomer && <span className="msg-via" title={sender}><Icon name={cm.via === 'portal' ? 'user' : 'mail'} size={12} /> {cm.via === 'portal' ? "dall'area clienti" : 'via email'}{sender && sender.toLowerCase() !== (ticket.recipient_email || '').toLowerCase() && <> · {sender}</>}</span>}
                      {isSent && <span className="msg-via msg-sent"><Icon name="send" size={12} /> Inviata al cliente</span>}
                      {isNote && <span className="msg-via">Visibile solo agli operatori</span>}
                      <time className="msg-time" title={fmtDateTime(c.created_at)}>{fmtDateTime(c.created_at)}</time>
                    </header>
                    <MessageText text={content} />
                    {renderAttachments(commentAtts)}
                  </div>
                </article>
              )
            })}

            {!hasOpening && comments.length === 0 && (
              <div className="empty" style={{ padding: '36px 24px' }}>
                <div className="empty-icon"><Icon name="message" size={20} /></div>
                <div className="empty-title">Nessun messaggio</div>
                <div className="empty-text" style={{ marginBottom: 0 }}>Scrivi la prima risposta qui sotto.</div>
              </div>
            )}
          </div>

          {/* Editor risposta */}
          <div className={`composer${isPublic ? '' : ' composer-note'}`}>
            <div className="composer-tabs">
              {canEmail && (
                <button className={`ctab${isPublic ? ' active' : ''}`} onClick={() => setReplyMode(true)}>
                  <Icon name="mail" size={15} /> Risposta pubblica
                </button>
              )}
              <button className={`ctab${!isPublic ? ' active note' : ''}`} onClick={() => setReplyMode(false)}>
                <Icon name="lock" size={15} /> Nota interna
              </button>
            </div>

            {notice && (
              <div className={`alert ${notice.type === 'error' ? 'alert-error' : 'alert-warning'}`} style={{ marginBottom: 10 }}>
                <Icon name="alert" size={16} />
                <span className="grow">{notice.text}</span>
                <button className="alert-close" onClick={() => setNotice(null)} title="Chiudi"><Icon name="x" size={14} /></button>
              </div>
            )}

            <div className={`composer-box${dragOver ? ' drag' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false) }}
              onDrop={handleDrop}>
              <div className="composer-meta">
                {isPublic
                  ? <><Icon name="send" size={13} /> A <strong>{ticket.recipient_email}</strong>{ccList.length > 0 && <> · Cc <strong title={ccList.join(', ')}>{ccList.length === 1 ? ccList[0] : `${ccList[0]} e altri ${ccList.length - 1}`}</strong></>}{outgoingMailbox && <> · da <strong>{outgoingMailbox}</strong></>}</>
                  : <><Icon name="lock" size={13} /> Visibile solo agli operatori, non viene inviata al cliente</>}
              </div>
              <textarea className="composer-input" value={newComment} onChange={e => setNewComment(e.target.value)}
                onPaste={handlePaste}
                onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canSend) { e.preventDefault(); addComment(isPublic) } }}
                placeholder={isPublic ? `Scrivi la risposta per ${requesterName(ticket.recipient_email)}…` : 'Scrivi una nota interna per i colleghi…'} />
              {pendingFiles.length > 0 && (
                <div className="composer-files">
                  {pendingFiles.map((f, i) => (
                    <span key={i} className="file-pill">
                      <FileIcon type={f.type} size={14} />
                      <span className="truncate">{f.name}</span>
                      <span className="faint">{formatSize(f.size)}</span>
                      <button onClick={() => removePendingFile(i)} title="Rimuovi"><Icon name="x" size={13} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="composer-foot">
              <label className="btn btn-ghost btn-sm" title={`Allega file (max ${MAX_FILE_MB} MB ciascuno) — puoi anche trascinarli qui o incollarli con Ctrl+V`}>
                <Icon name="paperclip" size={15} /> Allega
                <input type="file" multiple hidden onChange={handleFileSelect} />
              </label>
              <span className="kbd-hint">Trascina o incolla i file · <span className="kbd">Ctrl</span> + <span className="kbd">Invio</span> per inviare</span>
              <button className={`btn ${isPublic ? 'btn-primary' : 'btn-note'}`} onClick={() => addComment(isPublic)} disabled={!canSend}>
                {(sending || uploading) ? <Spinner size={14} /> : <Icon name={isPublic ? 'send' : 'lock'} size={15} />} {sendLabel}
              </button>
            </div>
          </div>
        </div>

        {/* Pannello laterale */}
        <aside className="detail-side">
          <section className="card panel">
            <h3 className="panel-title">Richiedente</h3>
            {canEmail ? (
              <div className="who">
                <Avatar name={ticket.recipient_email} size={38} />
                <div className="minw0">
                  <div className="strong truncate">{requesterName(ticket.recipient_email)}</div>
                  <a className="truncate" style={{ display: 'block', fontSize: 13 }} href={`mailto:${ticket.recipient_email}`}>{ticket.recipient_email}</a>
                </div>
              </div>
            ) : (
              <p className="muted" style={{ fontSize: 13 }}>Ticket interno: nessun cliente collegato via email.</p>
            )}
            {canEmail && ticket.cc_emails !== undefined && <CcEditor list={ccList} recipient={ticket.recipient_email} onSave={saveCc} />}
            {canEmail && <CustomerLink email={ticket.recipient_email} onOpenCustomers={onOpenCustomer} />}
          </section>

          <section className="card panel">
            <h3 className="panel-title">Proprietà</h3>
            {ticket.internal_ref !== undefined && <RefField value={ticket.internal_ref || ''} onSave={saveRef} />}
            <label className="field">
              <span className="label">Stato</span>
              <select className="select select-sm" value={st} onChange={e => updateTicket('status', e.target.value)}>
                <option value="aperto">Aperto</option>
                <option value="in_lavorazione">In lavorazione</option>
                <option value="risolto">Risolto</option>
              </select>
            </label>
            <label className="field" style={{ marginBottom: 12 }}>
              <span className="label">Assegnatario</span>
              <select className="select select-sm" value={ticket.assigned_to || ''} onChange={e => updateTicket('assigned_to', e.target.value || null)}>
                <option value="">— Non assegnato —</option>
                {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            {ticket.assigned_to !== profile.id && (
              <button className="btn btn-soft btn-sm btn-block" onClick={() => updateTicket('assigned_to', profile.id)}>
                <Icon name="userPlus" size={15} /> Assegna a me
              </button>
            )}
            {others.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div className="hint" style={{ marginBottom: 6 }}>Assegnazione rapida</div>
                <div className="quick">
                  {others.map(p => (
                    <button key={p.id} className="chip-btn" onClick={() => updateTicket('assigned_to', p.id)}>
                      <Avatar name={p.name} size={20} /> {p.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className="card panel">
            <h3 className="panel-title">Dettagli</h3>
            <dl className="props">
              <dt>Numero</dt><dd className="tnum">{ticket.number != null ? `#${ticket.number}` : '—'}</dd>
              <dt>Creato</dt><dd>{fmtDateTime(ticket.created_at)}</dd>
              <dt>Creato da</dt><dd>{ticket.created_by_profile?.name || '—'}</dd>
              <dt>Aggiornato</dt><dd>{timeAgo(ticket.updated_at || ticket.created_at)}</dd>
              <dt>Casella</dt><dd className="truncate" title={ticket.mailbox || ''}>{ticket.mailbox || '—'}</dd>
              <dt>Risposte da</dt><dd className="truncate" title={outgoingMailbox || ''}>{outgoingMailbox || '—'}</dd>
            </dl>
          </section>

          {attachments.length > 0 && (
            <section className="card panel">
              <h3 className="panel-title">Allegati <span className="badge">{attachments.length}</span></h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, margin: '0 -8px' }}>
                {attachments.map(att => (
                  <button key={att.id} className="att-list-item" onClick={() => downloadFile(att)} title={`Scarica ${att.file_name}`}>
                    <span className="att-list-ico">
                      {isPreviewable(att.file_type) && imageUrls[att.id]
                        ? <img src={imageUrls[att.id]} alt="" onError={() => handleImgError(att)} />
                        : <FileIcon type={att.file_type} size={16} />}
                    </span>
                    <span className="minw0 grow">
                      <span className="truncate" style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{att.file_name}</span>
                      <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{formatSize(att.file_size)}</span>
                    </span>
                    <Icon name="download" size={15} className="faint" />
                  </button>
                ))}
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  )
}
